import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { CalendarClock, CheckCheck, FileText, Inbox, RefreshCw, Sparkles } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { useVisiblePolling } from '../../hooks/useVisiblePolling';
import { StatTile } from '../ui/StatTile';
import { PartnerNoticeBar } from './PartnerNoticeBar';
import { PartnerReportList } from './PartnerReportList';
import {
  PARTNER_REPORT_SOURCES,
  type PartnerReportSource,
  type PartnerSummary,
  type PartnerUpdatesStatusFilter,
  type PartnerUpdatesSummary,
} from '../../types/partners';
import { reportSourceLabel } from '../../utils/partnerStatus';
import type { PartnerNotice } from '../../utils/partnerSync';

interface PartnerUpdatesInboxProps {
  canEdit: boolean;
  /** The viewer can open Modpack Configs, for "Update the cluster". */
  canOpenConfigs: boolean;
}

const STATUS_FILTERS: { id: PartnerUpdatesStatusFilter; label: string }[] = [
  { id: 'open', label: 'Waiting on staff' },
  { id: 'new', label: 'New' },
  { id: 'acknowledged', label: 'Acknowledged' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'live', label: 'Live' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'all', label: 'All reports' },
];

/**
 * Every partner's update reports in one place, newest first: what is new,
 * acknowledged or scheduled, filtered by partner and by where the report came
 * from. Staff move reports on from their row.
 */
export const PartnerUpdatesInbox = ({ canEdit, canOpenConfigs }: PartnerUpdatesInboxProps) => {
  const [status, setStatus] = useState<PartnerUpdatesStatusFilter>('open');
  const [source, setSource] = useState<PartnerReportSource | ''>('');
  const [partner, setPartner] = useState('');
  const [summary, setSummary] = useState<PartnerUpdatesSummary | null>(null);
  const [partners, setPartners] = useState<PartnerSummary[] | null>(null);
  const [notice, setNotice] = useState<PartnerNotice | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [focusId, setFocusId] = useState<string | null>(null);

  const loadSummary = useCallback(() => {
    partnersApi
      .updatesSummary()
      .then(setSummary)
      .catch(() => {
        // The tiles fall back to dashes; the list below says what went wrong.
      });
  }, []);

  useEffect(() => {
    loadSummary();
    partnersApi
      .list()
      .then(setPartners)
      .catch(() => setPartners(null));
  }, [loadSummary]);

  useVisiblePolling(loadSummary, 60_000);

  const sortedPartners = useMemo(() => [...(partners ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [partners]);
  const query = useMemo(
    () => ({ status, ...(source ? { source } : {}), ...(partner ? { creator_id: partner } : {}) }),
    [status, source, partner],
  );
  const filtered = status !== 'open' || !!source || !!partner;
  const toggleStatus = (next: PartnerUpdatesStatusFilter) => setStatus(status === next ? 'open' : next);

  return (
    <div className="space-y-4">
      <PartnerNoticeBar notice={notice} onDismiss={() => setNotice(null)} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="New"
          value={summary?.new ?? '—'}
          icon={Sparkles}
          tone={summary?.new ? 'info' : 'neutral'}
          active={status === 'new'}
          onClick={() => toggleStatus('new')}
          hint={summary?.oldest_new_at ? `oldest ${formatDistanceToNow(new Date(summary.oldest_new_at), { addSuffix: true })}` : 'none waiting'}
          loading={!summary}
        />
        <StatTile
          label="Acknowledged"
          value={summary?.acknowledged ?? '—'}
          icon={CheckCheck}
          tone="accent"
          active={status === 'acknowledged'}
          onClick={() => toggleStatus('acknowledged')}
          hint="seen, not planned yet"
          loading={!summary}
        />
        <StatTile
          label="Scheduled"
          value={summary?.scheduled ?? '—'}
          icon={CalendarClock}
          tone="purple"
          active={status === 'scheduled'}
          onClick={() => toggleStatus('scheduled')}
          hint="planned for our servers"
          loading={!summary}
        />
        <StatTile
          label="Waiting on staff"
          value={summary?.open ?? '—'}
          icon={Inbox}
          tone={summary?.open ? 'warning' : 'neutral'}
          active={status === 'open'}
          onClick={() => setStatus('open')}
          hint="new, acknowledged or scheduled"
          loading={!summary}
        />
      </div>

      <div className="glass-card p-4 md:p-5 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <select className="glass-select" value={status} onChange={(e) => setStatus(e.target.value as PartnerUpdatesStatusFilter)} aria-label="Status">
            {STATUS_FILTERS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
          <select className="glass-select" value={source} onChange={(e) => setSource(e.target.value as PartnerReportSource | '')} aria-label="Where the report came from">
            <option value="">From anywhere</option>
            {PARTNER_REPORT_SOURCES.map((s) => (
              <option key={s} value={s}>
                {s === 'curseforge' ? 'Found on CurseForge' : `From ${reportSourceLabel(s).label.toLowerCase()}`}
              </option>
            ))}
          </select>
          <select className="glass-select md:flex-1 md:max-w-xs" value={partner} onChange={(e) => setPartner(e.target.value)} aria-label="Partner" disabled={!partners}>
            <option value="">{partners ? 'Every partner' : 'Every partner (list unavailable)'}</option>
            {sortedPartners.map((p) => (
              <option key={p.creator_id} value={p.creator_id}>
                {p.name}
                {p.open_updates ? ` (${p.open_updates} open)` : ''}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2 md:ml-auto">
            {filtered && (
              <button
                type="button"
                className="btn-ghost btn-xs"
                onClick={() => {
                  setStatus('open');
                  setSource('');
                  setPartner('');
                }}
              >
                Clear filters
              </button>
            )}
            <button
              type="button"
              className="glass-button glass-button-sm"
              onClick={() => {
                loadSummary();
                setRefreshKey((k) => k + 1);
              }}
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        <PartnerReportList
          query={query}
          showPartner
          canEdit={canEdit}
          canOpenConfigs={canOpenConfigs}
          focusId={focusId}
          onFocusChange={setFocusId}
          onNotice={setNotice}
          refreshKey={refreshKey}
          onChanged={loadSummary}
          emptyTitle={status === 'open' && !filtered ? 'Nothing is waiting on staff' : 'No reports match these filters'}
          emptyDescription={
            status === 'open' && !filtered
              ? 'Every partner update has been dealt with. New reports from partner channels, and versions found on CurseForge first, land here.'
              : 'Try another status or partner.'
          }
        />
        <p className="text-xs text-glass-500 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" />
          Open a report for its changelog, notes, CurseForge listing, live status and timeline.
        </p>
      </div>
    </div>
  );
};
